/**
 * Higher-Order Component that provides router props to class components
 *
 * This is a replacement for the withRouter HOC from React Router 3/5
 * that works with React Router 6.
 *
 * Usage:
 *   export default withRouterV6(MyClassComponent)
 *
 * Props provided:
 *   - navigate: Navigation function
 *   - location: Current location object
 *   - params: Route parameters
 *   - query: Query parameters (parsed from search string)
 *   - searchParams: URLSearchParams instance
 */

import React from 'react'
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams
} from 'react-router-dom'

/**
 * HOC that injects router props into a component
 * @param {React.Component} WrappedComponent - The component to wrap
 * @returns {React.Component} Component with router props
 */
const withRouterV6 = WrappedComponent => {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component'

  const WithRouterV6 = props => {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()
    const [searchParams, setSearchParams] = useSearchParams()

    // Parse query params into an object (RR3 compatibility)
    const query = {}
    searchParams.forEach((value, key) => {
      query[key] = value
    })

    // Create push/replace functions compatible with RR3 API
    const push = (to, state) => {
      if (typeof to === 'string') {
        navigate(to, { state })
      } else {
        // Handle object format: { pathname, query, state }
        const { pathname = '/', query: queryParams, state: toState } = to
        let path = pathname
        if (queryParams) {
          const urlParams = new URLSearchParams()
          Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null)
              urlParams.append(key, value)
          })
          const queryString = urlParams.toString()
          if (queryString) path = `${pathname}?${queryString}`
        }
        navigate(path, { state: toState || state })
      }
    }

    const replace = (to, state) => {
      if (typeof to === 'string') {
        navigate(to, { replace: true, state })
      } else {
        const { pathname = '/', query: queryParams, state: toState } = to
        let path = pathname
        if (queryParams) {
          const urlParams = new URLSearchParams()
          Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null)
              urlParams.append(key, value)
          })
          const queryString = urlParams.toString()
          if (queryString) path = `${pathname}?${queryString}`
        }
        navigate(path, { replace: true, state: toState || state })
      }
    }

    const goBack = () => navigate(-1)
    const goForward = () => navigate(1)

    // Enhanced location with query (RR3 compatibility)
    const enhancedLocation = {
      ...location,
      query
    }

    // Create a router object similar to RR3
    const router = {
      push,
      replace,
      goBack,
      goForward,
      location: enhancedLocation,
      setRouteLeaveHook: () => {
        console.warn(
          'setRouteLeaveHook is not supported in RR6. Use useBlocker hook instead.'
        )
      }
    }

    return (
      <WrappedComponent
        {...props}
        navigate={navigate}
        location={enhancedLocation}
        params={params}
        query={query}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        router={router}
      />
    )
  }

  WithRouterV6.displayName = `withRouterV6(${displayName})`

  return WithRouterV6
}

export default withRouterV6
